import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, Search } from "lucide-react";

type DLRow = {
  id: string;
  user_id: string | null;
  organization_id: string | null;
  type: string;
  title: string;
  body: string | null;
  data: any;
  last_error: string | null;
  attempts: number;
  channel: string;
  created_at: string;
  resolved_at: string | null;
};

type TokenStatus = "any" | "invalid" | "unregistered" | "auth_failed" | "transient";

function classifyToken(err: string | null): Exclude<TokenStatus, "any"> {
  const e = (err ?? "").toLowerCase();
  if (e.includes("unregistered") || e.includes("404")) return "unregistered";
  if (e.includes("invalid") || e.startsWith("400") || e.includes(":400")) return "invalid";
  if (e.includes("auth_failed") || e.includes("403") || e.includes("permission")) return "auth_failed";
  return "transient";
}

export default function AdminNotificationsHealth() {
  const { user } = useAuth();
  const [rows, setRows] = useState<DLRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<DLRow | null>(null);
  const [sendingTest, setSendingTest] = useState(false);

  const [userFilter, setUserFilter] = useState("");
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>("any");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    setLoading(true);
    let q = (supabase as any)
      .from("notification_dead_letter")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (from) q = q.gte("created_at", new Date(from).toISOString());
    if (to) q = q.lte("created_at", new Date(to).toISOString());
    if (userFilter.trim()) q = q.eq("user_id", userFilter.trim());
    const { data } = await q;
    setRows((data ?? []) as DLRow[]);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(
    () => rows.filter((r) => tokenStatus === "any" || classifyToken(r.last_error) === tokenStatus),
    [rows, tokenStatus],
  );

  const counts = useMemo(() => {
    const since = Date.now() - 24 * 3600 * 1000;
    return {
      total: filtered.length,
      unresolved: filtered.filter((r) => !r.resolved_at).length,
      last24h: filtered.filter((r) => new Date(r.created_at).getTime() >= since).length,
    };
  }, [filtered]);

  const resolve = async (id: string) => {
    await (supabase as any).from("notification_dead_letter").update({ resolved_at: new Date().toISOString() }).eq("id", id);
    toast({ title: "Marked resolved" });
    load();
  };

  const sendTest = async () => {
    if (!user) return;
    setSendingTest(true);
    try {
      const { data, error } = await supabase.functions.invoke("notify", {
        body: {
          user_ids: [user.id],
          type: "student_activity",
          title: "WeKIT test notification",
          body: `Test dispatched at ${new Date().toLocaleTimeString()} — if you see this in-app and on device, FCM is healthy.`,
          data: { source: "notifications_health_test" },
        },
      });
      if (error) throw error;
      const fcm = (data as any)?.fcm;
      toast({
        title: "Test sent",
        description: fcm?.configured
          ? `In-app ✓  · FCM: sent ${fcm.sent}, failed ${fcm.failed}${fcm.dead_letters ? `, DLQ ${fcm.dead_letters}` : ""}`
          : "In-app ✓ · FCM not configured (no service account).",
      });
      load();
    } catch (e: any) {
      toast({ title: "Test failed", description: e.message, variant: "destructive" });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <>
      <PageHead title="Notification Health | WeKIT Admin" description="Dead letter queue and delivery health" path="/admin/notifications/health" />
      <div className="container py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold">Notification Health</h1>
            <p className="text-sm text-muted-foreground">In-app + FCM delivery. Failures that survive retries land here.</p>
          </div>
          <Button onClick={sendTest} disabled={sendingTest}>
            <Send className="w-4 h-4 mr-2" />{sendingTest ? "Sending…" : "Send test notification"}
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Kpi label="Dead-lettered (filtered)" value={counts.total} />
          <Kpi label="Unresolved" value={counts.unresolved} tone={counts.unresolved > 0 ? "warn" : "ok"} />
          <Kpi label="Last 24 hours" value={counts.last24h} />
        </div>

        <Card>
          <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">User ID</Label>
              <Input placeholder="uuid…" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Token status</Label>
              <Select value={tokenStatus} onValueChange={(v) => setTokenStatus(v as TokenStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="invalid">Invalid (400)</SelectItem>
                  <SelectItem value="unregistered">Unregistered (404)</SelectItem>
                  <SelectItem value="auth_failed">Auth failed (403)</SelectItem>
                  <SelectItem value="transient">Transient / other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">From</Label>
              <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">To</Label>
              <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="md:col-span-4 flex gap-2">
              <Button onClick={load}><Search className="w-4 h-4 mr-2" />Apply filters</Button>
              <Button variant="outline" onClick={() => { setUserFilter(""); setTokenStatus("any"); setFrom(""); setTo(""); setTimeout(load, 0); }}>Reset</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Failures</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-muted-foreground text-sm">No failures match. All quiet.</p>
            ) : (
              <div className="divide-y">
                {filtered.map((r) => {
                  const status = classifyToken(r.last_error);
                  return (
                    <div key={r.id} className="py-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setDrill(r)}>
                        <p className="font-medium text-sm truncate">{r.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {r.type} · {r.channel} · <span className="uppercase">{status}</span> · attempts {r.attempts} · {new Date(r.created_at).toLocaleString()}
                        </p>
                        {r.user_id && <p className="text-[11px] text-muted-foreground font-mono truncate">user {r.user_id}</p>}
                        {r.last_error && <p className="text-xs text-red-600 mt-1 truncate">{r.last_error}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setDrill(r)}>Details</Button>
                      {r.resolved_at ? (
                        <span className="text-xs text-green-600 self-center">Resolved</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => resolve(r.id)}>Resolve</Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{drill?.title}</DialogTitle></DialogHeader>
          {drill && (
            <div className="space-y-3 text-sm">
              <Row k="Type" v={drill.type} />
              <Row k="Channel" v={drill.channel} />
              <Row k="Token status" v={classifyToken(drill.last_error)} />
              <Row k="Attempts" v={String(drill.attempts)} />
              <Row k="User" v={drill.user_id ?? "—"} />
              <Row k="Organization" v={drill.organization_id ?? "—"} />
              <Row k="Created" v={new Date(drill.created_at).toLocaleString()} />
              {drill.body && <Row k="Body" v={drill.body} />}
              <div>
                <Label className="text-xs">Last error</Label>
                <pre className="mt-1 bg-muted rounded p-2 text-xs whitespace-pre-wrap break-all">{drill.last_error}</pre>
              </div>
              <div>
                <Label className="text-xs">Payload data</Label>
                <pre className="mt-1 bg-muted rounded p-2 text-xs whitespace-pre-wrap break-all">
{JSON.stringify(drill.data, null, 2)}
                </pre>
              </div>
              {!drill.resolved_at && (
                <Button onClick={() => { resolve(drill.id); setDrill(null); }}>Mark resolved</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${tone === "warn" ? "text-amber-600" : ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-3">
      <span className="text-xs uppercase tracking-wide text-muted-foreground w-28 shrink-0">{k}</span>
      <span className="text-sm break-all">{v}</span>
    </div>
  );
}
